'use client';

import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import { AI_TOOLKIT_CATEGORIES, AiToolkitSubmitStatus } from '@app/(darkHeader)/ai/_shared/_context/types';
import { useObjectUrl } from '@app/_shared/hooks/useObjectUrl';
import { type JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { getAccessibleFocusStyles, Text } from '@videoblocks/design-system';
import { GenerateVideo } from '@videoblocks/design-system/icons';
import { Play, WarningTriangle } from '@videoblocks/react-icons';

type ActiveStatus = Exclude<AiToolkitSubmitStatus, 'idle'>;

const SUB_LABELS: Record<ActiveStatus, string> = {
    inProgress: 'Generating…',
    success: 'Tap to view',
    error: 'Tap to retry',
};

export const CurrentGenerationCta = (): JSX.Element | null => {
    const { submitStates, formStates, imageSearch } = useAiToolkitContext();
    const { status, videoUrl } = submitStates[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION];
    const file = formStates[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]?.file ?? null;
    const startFrameUrl = useObjectUrl(file);

    if (status === 'idle') {
        return null;
    }

    return (
        <button
            type="button"
            onClick={() => imageSearch.setShowSearch(false)}
            data-testid="current-generation-cta"
            className={twMerge(
                'flex items-center gap-3 self-end p-1 rounded-sm',
                'w-[211px] h-[51px] shrink-0',
                'dark:bg-neutral-dark-200 hover:opacity-90 transition-opacity',
                getAccessibleFocusStyles(),
            )}
        >
            <Thumbnail status={status} videoUrl={videoUrl} startFrameUrl={startFrameUrl} />
            <div className="flex flex-col items-start text-left leading-tight">
                <Text fontSize="sm" fontWeight="semibold" className="text-neutral-dark-1000">
                    Current generation
                </Text>
                <Text fontSize="xs" fontWeight="regular" className="text-neutral-dark-700">
                    {SUB_LABELS[status]}
                </Text>
            </div>
        </button>
    );
};

/* ——— Subcomponents —————————————————————————————————————————————————————— */

type ThumbnailProps = {
    status: ActiveStatus;
    videoUrl: string;
    startFrameUrl: string;
};

function Thumbnail({ status, videoUrl, startFrameUrl }: ThumbnailProps): JSX.Element {
    const wrapperClassName =
        'size-10 rounded-sm overflow-hidden bg-neutral-dark-700 relative shrink-0 flex items-center justify-center';

    if (status === 'error') {
        return (
            <div className={wrapperClassName} data-testid="current-generation-cta-error">
                <WarningTriangle className="size-4 dark:[&_path]:fill-neutral-dark-1000" />
            </div>
        );
    }

    return (
        <div className={wrapperClassName}>
            <ThumbnailContent status={status} videoUrl={videoUrl} startFrameUrl={startFrameUrl} />
            {status === 'inProgress' && (
                <div className="absolute inset-0 bg-black/40" data-testid="current-generation-cta-loading" />
            )}
            {status === 'success' && (startFrameUrl || videoUrl) && (
                <div
                    className="absolute inset-0 flex items-center justify-center"
                    data-testid="current-generation-cta-play-overlay"
                >
                    <Play className="size-3 dark:[&_path]:fill-neutral-dark-1000" />
                </div>
            )}
        </div>
    );
}

function ThumbnailContent({ status, videoUrl, startFrameUrl }: ThumbnailProps): JSX.Element {
    if (startFrameUrl) {
        return (
            // eslint-disable-next-line @next/next/no-img-element
            <img
                src={startFrameUrl}
                alt=""
                className="size-full object-cover"
                data-testid="current-generation-cta-start-frame"
            />
        );
    }

    if (status === 'success' && videoUrl) {
        return (
            <video
                src={videoUrl}
                preload="metadata"
                muted
                playsInline
                className="size-full object-cover"
                data-testid="current-generation-cta-video-poster"
            />
        );
    }

    return (
        <GenerateVideo
            width={16}
            height={16}
            className="dark:[&_path]:fill-neutral-dark-1000"
            data-testid="current-generation-cta-placeholder"
        />
    );
}
